import { ArrowRight, BadgeCheck, BriefcaseBusiness, CalendarClock, CircleDollarSign, MapPin, MessageCircle, Search, Sparkles, TrendingUp, UsersRound, Zap } from 'lucide-react'
import { FormEvent, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AthleteCard } from '../../components/athletes/AthleteCard'
import { BriefOpportunityCard } from '../../components/briefs/BriefOpportunityCard'
import { CompanyCard } from '../../components/companies/CompanyCard'
import { InteractiveJourney } from '../../components/how/InteractiveJourney'
import { ButtonLink } from '../../components/ui/Button'
import { athletes, briefs, companies } from '../../data/demo'

const quickSearches = ['Triathlon à Annecy', 'Athlète handisport', 'Budget moins de 3 000 €', 'Auvergne-Rhône-Alpes']

export function HomePage() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const featured = athletes.slice(0, 6)
  const activeBriefs = briefs.filter((brief) => brief.status === 'active').slice(0, 4)
  const spotlight = useMemo(() => athletes.slice(0, 3), [])

  const submitSearch = (event: FormEvent) => {
    event.preventDefault()
    navigate(`/athletes${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`)
  }

  return <>
    <section className="modern-hero">
      <div className="modern-hero__aurora" aria-hidden="true" />
      <div className="container modern-hero__grid">
        <div className="modern-hero__copy">
          <span className="hero-live"><i />Des profils disponibles cette semaine</span>
          <h1>Le bon partenaire sportif est peut-être déjà <span>près de vous.</span></h1>
          <p>Recherchez un athlète par sport, territoire ou budget. Commencez par un échange, pas par un contrat.</p>
          <form className="smart-search" onSubmit={submitSearch}>
            <Search size={22} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Essayez « triathlon à Annecy »" aria-label="Rechercher un profil" />
            <button type="submit">Explorer <ArrowRight size={17} /></button>
          </form>
          <div className="search-suggestions"><span>Recherches rapides</span>{quickSearches.map((item) => <button onClick={() => { setQuery(item); navigate(`/athletes?q=${encodeURIComponent(item)}`) }} key={item}>{item}</button>)}</div>
          <div className="hero-metrics">
            <div><strong>{athletes.length}</strong><span>profils à découvrir</span></div>
            <div><strong>{activeBriefs.length}</strong><span>opportunités actives</span></div>
            <div><strong>&lt; 24 h</strong><span>réponse moyenne</span></div>
          </div>
        </div>

        <div className="spotlight-stage" aria-label="Aperçu des profils">
          <div className="spotlight-stage__orbit" aria-hidden="true" />
          {spotlight.map((athlete, index) => <Link className={`spotlight-card spotlight-card--${index + 1}`} to={`/athletes/${athlete.slug}`} key={athlete.id}>
            <img src={athlete.cover} alt={`${athlete.firstName} ${athlete.lastName}`} />
            <div className="spotlight-card__shade" />
            <div className="spotlight-card__top"><span className="availability-pill"><i />Disponible</span><span>{athlete.city}</span></div>
            <div className="spotlight-card__body"><small>{athlete.discipline} · {athlete.level}</small><strong>{athlete.firstName} {athlete.lastName}</strong><span>{(athlete.budgetNeeded - athlete.fundedAmount).toLocaleString('fr-FR')} € recherchés</span></div>
            <span className="spotlight-card__arrow"><ArrowRight /></span>
          </Link>)}
          <div className="spotlight-note"><Sparkles /><div><strong>Profils qualifiés</strong><span>Sport, ville, budget et disponibilité.</span></div></div>
        </div>
      </div>
    </section>

    <section className="market-pulse" aria-label="Activité de la marketplace">
      <div className="container market-pulse__inner"><span className="market-pulse__label"><Zap size={15} />En ce moment</span><div className="pulse-items">
        <span><img src={athletes[0].photo} alt="" />Camille est disponible à Annecy</span>
        <span><BriefcaseBusiness size={16} />Une PME recherche un ambassadeur en Rhône-Alpes</span>
        <span><MessageCircle size={16} />3 nouveaux échanges aujourd’hui</span>
      </div></div>
    </section>

    <section className="section container home-discovery home-discovery--modern">
      <div className="section-heading section-heading--inline"><div><span className="eyebrow">Pour commencer</span><h2>Des profils qui peuvent vous correspondre</h2><p>La sélection évolue selon votre territoire, vos objectifs et votre budget.</p></div><ButtonLink to="/athletes" variant="ghost">Tout explorer <ArrowRight size={17} /></ButtonLink></div>
      <div className="modern-tabs" role="tablist"><button className="is-active"><Sparkles size={15} />Pour vous</button><button><MapPin size={15} />À proximité</button><button><TrendingUp size={15} />Nouveaux</button><button><CalendarClock size={15} />Bientôt en compétition</button></div>
      <div className="cards-grid cards-grid--3">{featured.map((athlete) => <AthleteCard athlete={athlete} key={athlete.id} />)}</div>
    </section>

    <section className="section modern-opportunities-section">
      <div className="container">
        <div className="section-heading section-heading--inline"><div><span className="eyebrow">Côté athlètes</span><h2>Des entreprises cherchent maintenant</h2><p>Consultez les briefs, posez une question et manifestez votre intérêt.</p></div><ButtonLink to="/opportunites" variant="ghost">Voir toutes les opportunités <ArrowRight size={17} /></ButtonLink></div>
        <div className="cards-grid cards-grid--2">{activeBriefs.map((brief) => <BriefOpportunityCard brief={brief} company={companies.find((company) => company.id === brief.companyId)} publicMode key={brief.id} />)}</div>
      </div>
    </section>

    <section className="section container">
      <InteractiveJourney />
    </section>

    <section className="section company-showcase-v4">
      <div className="container">
        <div className="section-heading section-heading--inline"><div><span className="eyebrow">Entreprises actives</span><h2>Découvrez qui cherche un athlète</h2><p>Chaque entreprise possède désormais un profil public, un univers de marque et des opportunités consultables.</p></div><ButtonLink to="/entreprises" variant="ghost">Voir toutes les entreprises <ArrowRight size={17} /></ButtonLink></div>
        <div className="company-grid-v4 company-grid-v4--home">{companies.slice(0, 4).map((company) => <CompanyCard company={company} key={company.id} />)}</div>
      </div>
    </section>

    <section className="trust-compact trust-compact--modern">
      <div className="container"><div><BadgeCheck /><span><strong>Profils vérifiés</strong><small>Informations contrôlées</small></span></div><div><UsersRound /><span><strong>Entreprises identifiées</strong><small>Décideurs et structures</small></span></div><div><MessageCircle /><span><strong>Échanges centralisés</strong><small>Contexte conservé</small></span></div><div><CircleDollarSign /><span><strong>Paiements sécurisés</strong><small>Après signature</small></span></div></div>
    </section>
  </>
}
